import { isAfter, parseISO } from 'date-fns';
import {
  Activity,
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Building2,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Clock,
  DollarSign,
  Eye,
  FileSpreadsheet,
  FileText,
  Filter,
  Flag,
  GitBranch,
  Loader2,
  Mail,
  Maximize2,
  MessageSquare,
  MoreVertical,
  Pencil,
  Phone,
  Pin,
  Plus,
  Receipt,
  RotateCcw,
  Search,
  Settings,
  StickyNote,
  Trash2,
  User,
  UserCheck,
  UserCog,
  X,
  XCircle,
  Zap,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { AddNoteModal, LogCallModal } from '@/components/collections/action-modals';
import { ConsolidatedActivityTimeline } from '@/components/collections/consolidated-activity-timeline';
import { KPICard } from '@/components/collections/kpi-card';
import { DisputeSendEmailModal } from '@/components/disputes/dispute-send-email-modal';
import { ResolveDisputeModal } from '@/components/disputes/resolve-dispute-modal';
import { FeatureFeedbackCollector } from '@/components/FeedbackCollector';
import { HelpSupportButton } from '@/components/help-support';
import { AddContactDialog } from '@/components/shared/add-contact-dialog';
import { ColumnConfigDialog, type ColumnDefinition } from '@/components/shared/column-config-dialog';
import { getCurrencyIcon } from '@/components/shared/currency-icon';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { UniqueTableNamesForColumnConfiguration } from '@/config/unique-table-names-for-column-configuration';
import { useCompany } from '@/contexts/CompanyContext';
import { useCustomerContactsList } from '@/hooks/useCustomerContacts';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useAddDisputeNote, useDisputeNotes } from '@/hooks/useDisputeNotes';
import { useDisputeRecentActivities } from '@/hooks/useDisputeRecentActivities';
import type { GetDisputesParams } from '@/hooks/useDisputesData';
import {
  useCreateDispute,
  useDisputePriorities,
  useDisputeTypeOptions,
  useQuickUpdateDisputes,
  useUpdateDispute,
  useUserOptions,
} from '@/hooks/useDisputesData';
import { useDisputesPageDataGraphQL } from '@/hooks/useDisputesGraphQL';
import { useDisputeTypeById, useDisputeTypesList } from '@/hooks/useDisputeTypesData';
import { useDisputeWorkflowStages } from '@/hooks/useDisputeWorkflowStages';
import { useScreenColumnPreferences } from '@/hooks/useScreenColumnPreferences';
import { useSessionSortPreference } from '@/hooks/useSessionSortPreference';
import { useCompanyUsersList } from '@/hooks/useUsersData';
import { contactDisplayName } from '@/lib/contact-display-name';
import { readStageAssignee } from '@/lib/dispute-assignee';
import { exportDisputesToExcel } from '@/lib/export-utils';
import { getPageNumbers } from '@/lib/pagination-utils';
import { getPersistedFilters, setPersistedFilters } from '@/lib/session-storage-filters';
import { clipCell } from '@/lib/table-cell-clip';
import { cn, formatCurrency, formatDate, formatDateTime, getCurrencySymbol } from '@/lib/utils';
import { getWorkflowForDisputeType } from '@/lib/utils/dispute-workflow-mapper';
import { getTotalLevelsForDisputeType } from '@/lib/utils/dispute-workflow-utils';
import type { CreateDisputeRequest, DisputeWorkflowStageResponse, UpdateDisputeRequest } from '@/types/api/disputes';
import type { CaseNote, CollectionActivity, CollectionCommunication } from '@/types/collections';
import type { DisputeUI } from '@/types/ui/disputes';

const DEFAULT_DISPUTE_COLUMNS: ColumnDefinition[] = [
  { id: 'disputeId', label: 'Dispute ID', visible: true, order: 0 },
  { id: 'customer', label: 'Customer', visible: true, order: 1 },
  { id: 'type', label: 'Type', visible: true, order: 2 },
  { id: 'amount', label: 'Amount', visible: true, order: 3 },
  { id: 'status', label: 'Status', visible: true, order: 4 },
  { id: 'priority', label: 'Priority', visible: true, order: 5 },
  { id: 'workflowProgress', label: 'Workflow Progress', visible: true, order: 6 },
  { id: 'assignedTo', label: 'Assigned To', visible: true, order: 7 },
  { id: 'dueDate', label: 'Due Date', visible: true, order: 8 },
  { id: 'createdDate', label: 'Created Date', visible: false, order: 9 },
  { id: 'workflow', label: 'Workflow', visible: false, order: 10 },
  { id: 'description', label: 'Description', visible: false, order: 11 },
];

const getContactPlaceholder = (customerId: string, contactCount: number) => {
  if (!customerId) return 'Select customer first';
  if (contactCount > 0) return 'Select contact';
  return 'No contacts available';
};

const statusColors = {
  New: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
  'Under Review': 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300',
  'In Progress': 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300',
  Escalated: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
  Resolved: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
  Closed: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300',
};

const priorityColors = {
  Low: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300',
  Medium: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
  High: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300',
  Critical: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
};

const DISPUTE_STATUS_OPTIONS = ['New', 'In Progress', 'Escalated', 'Resolved', 'Closed'] as const;

export const DISPUTES_ALL_FILTERS_KEY = 'disputes-all-filters';

export interface PersistedDisputesFilters {
  advancedFilters: AdvancedFilters;
  searchQuery: string;
}

interface AdvancedFilters {
  statuses: string[];
  priorities: string[];
  assignedUser: string;
  overdueOnly: boolean;
}

const DEFAULT_ADVANCED_FILTERS: AdvancedFilters = {
  statuses: ['New', 'In Progress', 'Escalated', 'Resolved'],
  priorities: [],
  assignedUser: 'all',
  overdueOnly: false,
};

const initialCreateFormState = {
  customerId: '',
  contactId: '',
  contactEmail: '',
  contactPhone: '',
  disputeTypeId: '',
  amount: '',
  statusId: '1',
  priorityId: '',
  assignedToUserId: '',
  dueDate: '',
  description: '',
};

// Resolve workflow stages for a dispute: prefer snapshot, fall back to live config, then hardcoded mapper
function resolveWorkflowStagesForDispute(
  dispute: DisputeUI,
  snapshotStages?: DisputeWorkflowStageResponse[],
): { level: number; title: string; description: string; estimatedDays?: number }[] {
  if (snapshotStages && snapshotStages.length > 0) {
    return [...snapshotStages]
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((stage) => {
        const props = stage.properties ? JSON.parse(stage.properties) : undefined;
        return {
          level: stage.sortOrder,
          title: stage.name,
          description: stage.description ?? '',
          estimatedDays: props?.slaHours ? Math.ceil(props.slaHours / 24) : undefined,
        };
      });
  }

  const fallbackWorkflow = getWorkflowForDisputeType(dispute.type);
  return fallbackWorkflow.stages;
}

// Resolve followUpDays for a given workflow level: prefer snapshot, fall back to live config
function resolveFollowUpDaysForLevel(
  level: number,
  snapshotStages?: DisputeWorkflowStageResponse[],
  fullDisputeType?: {
    workflow?: { stages?: Array<{ order: number; properties?: { followUpDays?: number; slaWarningHours?: number } }> };
  } | null,
): number | undefined {
  if (snapshotStages && snapshotStages.length > 0) {
    const stage = snapshotStages.find((s) => s.sortOrder === level);
    if (stage?.properties) {
      const props = JSON.parse(stage.properties);
      return props?.followUpDays ?? (props?.slaWarningHours ? Math.ceil(props.slaWarningHours / 24) : undefined);
    }
    return undefined;
  }

  if (fullDisputeType?.workflow?.stages) {
    const stage = [...fullDisputeType.workflow.stages].sort((a, b) => a.order - b.order).find((s) => s.order === level);
    return (
      stage?.properties?.followUpDays ??
      (stage?.properties?.slaWarningHours ? Math.ceil(stage.properties.slaWarningHours / 24) : undefined)
    );
  }

  return undefined;
}

function UpdatePriorityModal({
  open,
  onOpenChange,
  selectedCount,
  selectedDisputeId,
  priorities,
  value,
  onValueChange,
  onConfirm,
}: Readonly<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedCount: number;
  selectedDisputeId: string | undefined;
  priorities: Array<{ code: string; name: string; id?: number }>;
  value: string;
  onValueChange: (value: string) => void;
  onConfirm: () => void;
}>) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Flag className="h-5 w-5 text-primary" />
            Update Priority
          </DialogTitle>
          <DialogDescription>
            {selectedCount === 1 ? (
              <>
                Change the priority for dispute <span className="font-semibold">{selectedDisputeId}</span>
              </>
            ) : (
              <>
                Update the priority for <span className="font-semibold">{selectedCount} disputes</span>
              </>
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <Label>New Priority</Label>
          <Select value={value} onValueChange={onValueChange}>
            <SelectTrigger>
              <SelectValue placeholder="Select priority..." />
            </SelectTrigger>
            <SelectContent>
              {priorities.map((p) => (
                <SelectItem key={p.code} value={p.name}>
                  <div className="flex items-center gap-2">
                    <Badge className={priorityColors[p.name as keyof typeof priorityColors]}>{p.name}</Badge>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={onConfirm} disabled={!value} className="gap-2">
            <Flag className="h-4 w-4" />
            {selectedCount > 1 ? `Update ${selectedCount} Disputes` : 'Update Priority'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function DisputesAllPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { selectedCompany } = useCompany();
  const currencySymbol = getCurrencySymbol(selectedCompany?.currency || 'GBP');
  const persistedFilters = useMemo(() => getPersistedFilters<PersistedDisputesFilters>(DISPUTES_ALL_FILTERS_KEY), []);
  const [searchQuery, setSearchQuery] = useState(persistedFilters?.searchQuery ?? '');
  const debouncedSearch = useDebouncedValue(searchQuery);

  // Filter drawer state (matches Promises page pattern)
  const [moreFiltersOpen, setMoreFiltersOpen] = useState(false);
  const initialFilters = useMemo((): AdvancedFilters => {
    const statusParam = searchParams.get('status');
    const priorityParam = searchParams.get('priority');
    const overdueParam = searchParams.get('overdue');
    const assignedUserParam = searchParams.get('assignedUser');
    return {
      statuses: statusParam ? statusParam.split(',').filter(Boolean) : DEFAULT_ADVANCED_FILTERS.statuses,
      priorities: priorityParam ? priorityParam.split(',').filter(Boolean) : [],
      assignedUser: assignedUserParam || 'all',
      overdueOnly: overdueParam === 'true',
    };
  }, []);
  // When arriving via a dashboard drill-through the URL carries filter params;
  // those should win over any previously persisted filters.
  const hasUrlFilters = useMemo(() => searchParams.toString().length > 0, []); // eslint-disable-line react-hooks/exhaustive-deps -- read URL params only on mount
  const startFilters =
    !hasUrlFilters && persistedFilters?.advancedFilters ? persistedFilters.advancedFilters : initialFilters;
  const [advancedFilters, setAdvancedFilters] = useState<AdvancedFilters>(startFilters);
  const [draftFilters, setDraftFilters] = useState<AdvancedFilters>(startFilters);

  const handleOpenMoreFilters = useCallback(() => {
    setDraftFilters({ ...advancedFilters });
    setMoreFiltersOpen(true);
  }, [advancedFilters]);

  const handleApplyFilters = useCallback(() => {
    setAdvancedFilters({ ...draftFilters });
    setMoreFiltersOpen(false);
    setCurrentPage(1);
  }, [draftFilters]);

  const handleClearDraftFilters = useCallback(() => {
    setDraftFilters({ ...DEFAULT_ADVANCED_FILTERS });
  }, []);

  const handleCloseMoreFilters = useCallback((open: boolean) => {
    if (!open) {
      setMoreFiltersOpen(false);
    }
  }, []);

  const handleToggleDraftStatus = useCallback((status: string, checked: boolean) => {
    setDraftFilters((prev) => ({
      ...prev,
      statuses: checked ? [...prev.statuses, status] : prev.statuses.filter((s) => s !== status),
    }));
  }, []);

  const handleToggleDraftPriority = useCallback((priority: string, checked: boolean) => {
    setDraftFilters((prev) => ({
      ...prev,
      priorities: checked ? [...prev.priorities, priority] : prev.priorities.filter((pr) => pr !== priority),
    }));
  }, []);

  // Persist filter state to sessionStorage on change
  useEffect(() => {
    const state: PersistedDisputesFilters = { advancedFilters, searchQuery };
    setPersistedFilters(DISPUTES_ALL_FILTERS_KEY, state);
  }, [advancedFilters, searchQuery]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (JSON.stringify(advancedFilters.statuses) !== JSON.stringify(DEFAULT_ADVANCED_FILTERS.statuses)) count += 1;
    if (advancedFilters.priorities.length > 0) count += 1;
    if (advancedFilters.assignedUser !== 'all') count += 1;
    if (advancedFilters.overdueOnly) count += 1;
    return count;
  }, [advancedFilters]);

  // Server-side sorting state (persisted per screen for the user session)
  const [{ column: sortBy, direction: sortOrder }, setSort] = useSessionSortPreference<string, 'asc' | 'desc'>(
    UniqueTableNamesForColumnConfiguration.DisputeManagementAllDisputes,
    'createdAt',
    'desc',
  );

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [selectedDispute, setSelectedDispute] = useState<DisputeUI | null>(null);
  // Multi-select: array of selected dispute IDs
  const [selectedDisputeIds, setSelectedDisputeIds] = useState<string[]>([]);
  const [isColumnConfigOpen, setIsColumnConfigOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [logCallOpen, setLogCallOpen] = useState(false);
  const [sendEmailOpen, setSendEmailOpen] = useState(false);

  // Quick action modal states
  const [addNoteOpen, setAddNoteOpen] = useState(false);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [reassignUserId, setReassignUserId] = useState('');
  const [updatePriorityOpen, setUpdatePriorityOpen] = useState(false);
  const [updatePriorityValue, setUpdatePriorityValue] = useState('');
  const [closeDisputeOpen, setCloseDisputeOpen] = useState(false);
  const [closeReason, setCloseReason] = useState<'duplicate' | 'customer_withdrew' | 'invalid_dispute' | 'other'>(
    'duplicate',
  );
  const [closeNote, setCloseNote] = useState('');
  const [resolveDisputeOpen, setResolveDisputeOpen] = useState(false);
  const [reopenDisputeOpen, setReopenDisputeOpen] = useState(false);
  const [createForm, setCreateForm] = useState(initialCreateFormState);
  const [createFormContacts, setCreateFormContacts] = useState<
    Array<{ id: string; name: string; email: string; phone: string }>
  >([]);

  // Next Action Modal state
  const [isNextActionModalOpen, setIsNextActionModalOpen] = useState(false);
  const [nextActionNotes, setNextActionNotes] = useState('');
  const [nextActionFollowUpDate, setNextActionFollowUpDate] = useState('');

  const {
    columns: disputeColumns,
    visibleColumns: baseVisibleColumnDefs,
    savePreferences: saveDisputeColumnPreferences,
    resetToDefaults: resetDisputeColumnPreferences,
    columnOrder,
    setColumnOrder,
    columnWidths,
    setColumnWidths,
    pinnedLeft,
    pinnedRight,
    setPinned,
    resetColumnOrder,
    resetColumnWidths,
  } = useScreenColumnPreferences(
    UniqueTableNamesForColumnConfiguration.DisputeManagementAllDisputes,
    DEFAULT_DISPUTE_COLUMNS,
  );
  const baseVisibleColumns = baseVisibleColumnDefs.map((col) => col.id);
  const visibleColumns =
    columnOrder.length > 0
      ? columnOrder
          .filter((id) => baseVisibleColumns.includes(id))
          .concat(baseVisibleColumns.filter((id) => !columnOrder.includes(id)))
      : baseVisibleColumns;

  // Column resize state
  const resizingRef = useRef<{ columnId: string; startX: number; startWidth: number } | null>(null);
  const [localColumnWidths, setLocalColumnWidths] = useState<Record<string, number>>(columnWidths);
  const localColumnWidthsRef = useRef(localColumnWidths);
  localColumnWidthsRef.current = localColumnWidths;

  useEffect(() => {
    setLocalColumnWidths(columnWidths);
  }, [columnWidths]);

  const handleResizeMouseDown = (e: React.MouseEvent, columnId: string, currentWidth: number) => {
    e.preventDefault();
    e.stopPropagation();
    resizingRef.current = { columnId, startX: e.clientX, startWidth: currentWidth };
    const handleMouseMove = (ev: MouseEvent) => {
      if (!resizingRef.current) return;
      const { columnId: cId, startX, startWidth: sw } = resizingRef.current;
      const delta = ev.clientX - startX;
      const newWidth = Math.max(60, sw + delta);
      setLocalColumnWidths((prev) => ({ ...prev, [cId]: newWidth }));
    };
    const handleMouseUp = () => {
      if (resizingRef.current) {
        setColumnWidths(localColumnWidthsRef.current);
      }
      resizingRef.current = null;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // Column reorder state
  const [draggedColumn, setDraggedColumn] = useState<string | null>(null);
  const [dragOverColumn, setDragOverColumn] = useState<string | null>(null);

  const handlePinLeft = (columnId: string) => {
    const newPinnedLeft = pinnedLeft.includes(columnId)
      ? pinnedLeft.filter((id) => id !== columnId)
      : [...pinnedLeft, columnId];
    const newPinnedRight = pinnedRight.filter((id) => id !== columnId);
    setPinned(newPinnedLeft, newPinnedRight);
  };

  const handlePinRight = (columnId: string) => {
    const newPinnedRight = pinnedRight.includes(columnId)
      ? pinnedRight.filter((id) => id !== columnId)
      : [...pinnedRight, columnId];
    const newPinnedLeft = pinnedLeft.filter((id) => id !== columnId);
    setPinned(newPinnedLeft, newPinnedRight);
  };

  const handleResetColumnWidths = () => resetColumnWidths();
  const handleResetColumnOrder = () => resetColumnOrder();

  // Mode-aware contacts query for create form
  const createFormCustomerId = useMemo(() => createForm.customerId || '0', [createForm.customerId]);
  const { data: createFormContactsData } = useCustomerContactsList(createFormCustomerId);

  // Reset to page 1 when debounced search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch]);

  // Build server-side query params using advancedFilters
  const queryParams = useMemo<GetDisputesParams>(() => {
    const params: GetDisputesParams = {
      page: currentPage,
      pageSize,
      sortBy,
      sortOrder,
    };

    if (debouncedSearch) {
      params.search = debouncedSearch;
    }

    if (advancedFilters.statuses.length > 0) {
      params.statuses = advancedFilters.statuses;
    }

    if (advancedFilters.priorities.length > 0) {
      params.priorities = advancedFilters.priorities;
    }

    if (advancedFilters.assignedUser !== 'all') {
      const userId = Number(advancedFilters.assignedUser);
      if (!isNaN(userId)) params.assignedToUserId = userId;
    }

    if (advancedFilters.overdueOnly) {
      params.overdueOnly = true;
    }

    return params;
  }, [currentPage, pageSize, sortBy, sortOrder, debouncedSearch, advancedFilters]);

  const { disputes, pagination, totals, isLoading, refetch: refetchDisputes } = useDisputesPageDataGraphQL(queryParams);

  const { data: activeUsers = [] } = useCompanyUsersList();

  const userFilterOptions = useMemo(() => {
    return activeUsers
      .map((user) => ({
        id: user.id,
        displayName: `${user.firstName} ${user.lastName}`,
      }))
      .sort((a, b) => a.displayName.localeCompare(b.displayName));
  }, [activeUsers]);
  const createDisputeMutation = useCreateDispute();
  const { data: disputeTypeOptions = [] } = useDisputeTypeOptions();
  const { data: disputeTypesFullList = [] } = useDisputeTypesList();
  // Fetch full dispute type (with real workflow stages) for the selected dispute and create form
  const selectedDisputeTypeId = disputeTypesFullList.find((dt) => dt.name === selectedDispute?.type)?.id ?? null;
  const { data: fullSelectedDisputeType } = useDisputeTypeById(selectedDisputeTypeId);
  const { data: fullCreateFormDisputeType } = useDisputeTypeById(createForm.disputeTypeId || null);
  const { data: userOptions = [] } = useUserOptions();
  const { data: disputePriorities = [] } = useDisputePriorities();

  const selectedDisputeNumericId = selectedDispute ? parseInt(selectedDispute.id, 10) : 0;
  const updateDisputeMutation = useUpdateDispute(selectedDisputeNumericId);
  const { data: disputeActivityData, refetch: refetchActivities } = useDisputeRecentActivities(
    selectedDisputeNumericId ? selectedDisputeNumericId.toString() : undefined,
  );
  const { data: snapshotStages } = useDisputeWorkflowStages(selectedDisputeNumericId || null);
  const quickUpdateDisputes = useQuickUpdateDisputes();
  const addDisputeNoteMutation = useAddDisputeNote();
  const { data: apiDisputeNotes = [], refetch: refetchDisputeNotes } = useDisputeNotes(
    selectedDisputeNumericId ? selectedDisputeNumericId.toString() : undefined,
  );

  const disputeNotesForPanel = apiDisputeNotes.map((note) => ({
    id: note.id,
    caseId: note.disputeId,
    content: note.content,
    createdBy: note.createdBy.name,
    createdAt: new Date(note.createdAt),
  }));

  const [editForm, setEditForm] = useState({
    statusId: '',
    priorityId: '',
    assignedToUserId: '',
    dueDate: '',
    description: '',
    resolutionNotes: '',
    latestNote: '',
  });

  const handleCreateFormChange = (field: string, value: string) => {
    setCreateForm((prev) => ({ ...prev, [field]: value }));

    // Auto-populate email and phone when contact is selected
    if (field === 'contactId' && value) {
      const selectedContact = createFormContacts.find((c) => c.id === value);
      if (selectedContact) {
        setCreateForm((prev) => ({
          ...prev,
          contactEmail: selectedContact.email || '',
          contactPhone: selectedContact.phone || '',
        }));
      }
    }

    // Reset contact fields when customer changes
    if (field === 'customerId') {
      setCreateForm((prev) => ({ ...prev, contactId: '', contactEmail: '', contactPhone: '' }));
      setCreateFormContacts([]);
    }
  };

  // Sync contacts when customer is selected in create form
  useEffect(() => {
    if (createForm.customerId && createFormContactsData && createFormContactsData.length > 0) {
      const newContacts = createFormContactsData.map((c) => ({
        id: c.id,
        name: contactDisplayName(c),
        email: c.email,
        phone: c.phone,
      }));
      setCreateFormContacts(newContacts);
    } else if (!createForm.customerId) {
      setCreateFormContacts((prev) => (prev.length === 0 ? prev : []));
    }
  }, [createForm.customerId, createFormContactsData]);

  const handleEditFormChange = (field: string, value: string) => {
    setEditForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleUpdateDispute = async () => {
    if (!selectedDispute) return;

    const request: UpdateDisputeRequest = {};
    if (editForm.statusId) request.statusId = parseInt(editForm.statusId);
    if (editForm.priorityId) request.priorityId = parseInt(editForm.priorityId);
    if (editForm.assignedToUserId) request.assignedToUserId = parseInt(editForm.assignedToUserId);
    if (editForm.dueDate) request.dueDate = editForm.dueDate;
    if (editForm.description) request.description = editForm.description;
    if (editForm.resolutionNotes) request.resolutionNotes = editForm.resolutionNotes;

    // If latestNote is provided, it will be added as a new note in the Activity tab
    // In a real implementation, this would be sent to the backend to create a new note
    if (editForm.latestNote) {
    }

    try {
      await updateDisputeMutation.mutateAsync(request);
      setEditForm({
        statusId: '',
        priorityId: '',
        assignedToUserId: '',
        dueDate: '',
        description: '',
        resolutionNotes: '',
        latestNote: '',
      });
      refetchDisputes();
    } catch (error) {
      console.error('Failed to update dispute:', error);
    }
  };

  const handleCreateDispute = async () => {
    if (!createForm.customerId || !createForm.disputeTypeId || !createForm.amount || !createForm.priorityId) {
      return;
    }

    // Calculate DueDate from level 1's followUpDays of the selected dispute type's workflow
    // Use fullCreateFormDisputeType (from useDisputeTypeById) which has real workflow stages;
    // disputeTypesFullList from useDisputeTypesList only has placeholder workflows without followUpDays.
    let calculatedDueDate: string | undefined;
    if (fullCreateFormDisputeType?.workflow?.stages) {
      const sortedStages = [...fullCreateFormDisputeType.workflow.stages].sort((a, b) => a.order - b.order);
      const stage1 = sortedStages[0];
      const followUpDays =
        stage1?.properties?.followUpDays ??
        (stage1?.properties?.slaWarningHours ? Math.ceil(stage1.properties.slaWarningHours / 24) : undefined);
      if (followUpDays && typeof followUpDays === 'number') {
        const due = new Date();
        due.setDate(due.getDate() + followUpDays);
        calculatedDueDate = due.toISOString().split('T')[0];
      }
    }

    const request: CreateDisputeRequest = {
      customerId: parseInt(createForm.customerId),
      disputeTypeId: parseInt(createForm.disputeTypeId),
      amount: parseFloat(createForm.amount),
      statusId: parseInt(createForm.statusId),
      priorityId: parseInt(createForm.priorityId),
      assignedToUserId: createForm.assignedToUserId ? parseInt(createForm.assignedToUserId) : undefined,
      dueDate: calculatedDueDate || createForm.dueDate || undefined,
      description: createForm.description || undefined,
      contactId: createForm.contactId ? parseInt(createForm.contactId) : undefined,
    };

    try {
      await createDisputeMutation.mutateAsync(request);
      setCreateForm(initialCreateFormState);
      setCreateFormContacts([]);
      refetchDisputes();
    } catch (error) {
      console.error('Failed to create dispute:', error);
    }
  };

  // Map column IDs to backend sort field names
  const sortableColumnMap = useMemo<Record<string, string>>(
    () => ({
      disputeId: 'disputeId',
      customer: 'customer',
      type: 'type',
      amount: 'amount',
      status: 'status',
      priority: 'priority',
      assignedTo: 'assignedTo',
      dueDate: 'dueDate',
      createdDate: 'createdAt',
      workflow: 'workflow',
    }),
    [],
  );

  const renderDueDateCell = (dispute: DisputeUI) => {
    if (!dispute.dueDate) return '—';
    const dueDate = new Date(dispute.dueDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dueDateNorm = new Date(dueDate);
    dueDateNorm.setHours(0, 0, 0, 0);
    const isOverdue = dueDateNorm < today;
    const isDueToday = dueDateNorm.getTime() === today.getTime();
    let dueDateColorClass = 'text-muted-foreground';
    if (isOverdue) {
      dueDateColorClass = 'text-red-600 dark:text-red-400 font-medium';
    } else if (isDueToday) {
      dueDateColorClass = 'text-amber-600 dark:text-amber-400 font-medium';
    }
    return <span className={cn(dueDateColorClass)}>{formatDate(dispute.dueDate)}</span>;
  };

  const renderWorkflowProgressCell = (dispute: DisputeUI) => {
    const totalLevels = dispute.totalLevels || getTotalLevelsForDisputeType(dispute.type);
    return (
      <div className="flex items-center gap-2">
        <div className="bg-gray-200 dark:bg-gray-700 rounded-full h-2 w-[100px] flex-shrink-0 overflow-hidden">
          <div
            className="bg-primary h-2 rounded-full"
            style={{ width: `${(dispute.currentLevel / totalLevels) * 100}%` }}
          />
        </div>
        <span className="text-xs text-muted-foreground whitespace-nowrap">
          {dispute.currentLevel}/{totalLevels}
        </span>
      </div>
    );
  };

  const renderColumnCell = (columnId: string, dispute: DisputeUI) => {
    switch (columnId) {
      case 'disputeId':
        return (
          <button onClick={() => handleViewDispute(dispute)} className="font-medium text-primary hover:underline">
            {dispute.disputeNumber}
          </button>
        );
      case 'customer':
        return dispute.customer;
      case 'type':
        return dispute.type;
      case 'amount':
        return formatCurrency(dispute.amount, dispute.currencyCode || selectedCompany?.currency || 'GBP');
      case 'status':
        return <Badge className={statusColors[dispute.status as keyof typeof statusColors]}>{dispute.status}</Badge>;
      case 'priority':
        return (
          <Badge className={priorityColors[dispute.priority as keyof typeof priorityColors]}>{dispute.priority}</Badge>
        );
      case 'workflowProgress':
        return renderWorkflowProgressCell(dispute);
      case 'assignedTo':
        return dispute.assignedTo;
      case 'dueDate':
        return renderDueDateCell(dispute);
      case 'createdDate':
        return formatDateTime(dispute.createdDate);
      case 'workflow':
        return dispute.workflow;
      case 'description':
        return (
          <span className="truncate max-w-[200px] block" title={dispute.description}>
            {dispute.description}
          </span>
        );
      default:
        return null;
    }
  };

  // Apply overdue post-filter on already-paginated results
  const filteredDisputes = useMemo(() => {
    if (!advancedFilters.overdueOnly) return disputes;
    return disputes.filter((x) => x.dueDate && isAfter(new Date(), parseISO(x.dueDate)));
  }, [disputes, advancedFilters.overdueOnly]);

  // Derive the statuses of all selected disputes and determine quick action visibility
  const { selectedDisputeObjects, shouldHideAllActions, shouldShowReopenOnly } = useMemo(() => {
    const objs = selectedDisputeIds
      .map((id) => filteredDisputes.find((d) => d.id === id))
      .filter((d): d is DisputeUI => d !== undefined);
    const hasAnyResolved = objs.some((d) => d.status === 'Resolved');
    const allClosed = objs.length > 0 && objs.every((d) => d.status === 'Closed');
    const hasAnyClosed = objs.some((d) => d.status === 'Closed');
    const hasMixedWithClosed = hasAnyClosed && !allClosed;
    return {
      selectedDisputeObjects: objs,
      shouldHideAllActions: hasAnyResolved || hasMixedWithClosed,
      shouldShowReopenOnly: allClosed,
    };
  }, [selectedDisputeIds, filteredDisputes]);

  const renderActionsDropdown = () => {
    if (shouldShowReopenOnly) {
      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="secondary" size="sm" className="gap-2">
              Actions
              <ChevronDown className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            <DropdownMenuItem onClick={() => setReopenDisputeOpen(true)} className="gap-2">
              <RotateCcw className="h-4 w-4" />
              Reopen
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      );
    }

    if (shouldHideAllActions) return null;

    const isSingle = selectedDisputeIds.length === 1;

    // ── Single dispute: primary inline buttons + More overflow dropdown ──
    if (isSingle) {
      return (
        <>
          <Button variant="secondary" size="sm" onClick={() => setReassignOpen(true)} className="gap-2">
            <UserCog className="h-4 w-4" />
            Reassign User
          </Button>
          <Button variant="secondary" size="sm" onClick={() => setUpdatePriorityOpen(true)} className="gap-2">
            <Flag className="h-4 w-4" />
            Update Priority
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="secondary" size="sm" className="gap-2">
                More
                <ChevronDown className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuItem onClick={() => setAddNoteOpen(true)} className="gap-2">
                <StickyNote className="h-4 w-4" />
                Add Note
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setSendEmailOpen(true)} className="gap-2">
                <Mail className="h-4 w-4" />
                Send Email
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setLogCallOpen(true)} className="gap-2">
                <Phone className="h-4 w-4" />
                Log Call
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setResolveDisputeOpen(true)} className="gap-2">
                <CheckCircle2 className="h-4 w-4" />
                Resolve Dispute
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setCloseDisputeOpen(true)} className="gap-2">
                <XCircle className="h-4 w-4" />
                Close Dispute
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </>
      );
    }

    // ── Multiple disputes: inline action buttons ──────────────────────────
    return (
      <>
        <Button variant="secondary" size="sm" onClick={() => setReassignOpen(true)} className="gap-2">
          <UserCog className="h-4 w-4" />
          Reassign User
        </Button>
        <Button variant="secondary" size="sm" onClick={() => setUpdatePriorityOpen(true)} className="gap-2">
          <Flag className="h-4 w-4" />
          Update Priority
        </Button>
      </>
    );
  };

  const renderReopenDialogDescription = () => {
    const statusText = selectedDisputeObjects.some((d) => d.currentLevel !== 1)
      ? 'its appropriate status based on workflow level'
      : 'New status';
    if (selectedDisputeIds.length === 1) {
      return (
        <>
          Are you sure you want to reopen dispute <span className="font-semibold">{selectedDisputeIds[0]}</span>? Each
          dispute will be set to {statusText}.
        </>
      );
    }
    return (
      <>
        Are you sure you want to reopen <span className="font-semibold">{selectedDisputeIds.length} disputes</span>?
        Each dispute will be set to {statusText}.
      </>
    );
  };

  // Server-side sorting handlers
  const handleSort = useCallback(
    (columnId: string) => {
      const backendField = sortableColumnMap[columnId];
      if (!backendField) return;

      if (sortBy === backendField) {
        if (sortOrder === 'asc') {
          setSort(backendField, 'desc');
        } else {
          // Reset to default sort
          setSort('createdAt', 'desc');
        }
      } else {
        setSort(backendField, 'asc');
      }
      setCurrentPage(1);
    },
    [sortBy, sortOrder, sortableColumnMap, setSort],
  );

  const getSortIcon = useCallback(
    (columnId: string) => {
      const backendField = sortableColumnMap[columnId];
      if (!backendField) return null;

      if (sortBy !== backendField) {
        return <ArrowUpDown className="ml-2 h-4 w-4" />;
      }
      if (sortOrder === 'asc') {
        return <ArrowUp className="ml-2 h-4 w-4" />;
      }
      return <ArrowDown className="ml-2 h-4 w-4" />;
    },
    [sortBy, sortOrder, sortableColumnMap],
  );

  // Server-side pagination values
  const totalRecords = pagination.totalItems;
  const totalPages = pagination.totalPages;

  // Page reset when advancedFilters change is handled inside handleApplyFilters

  // Reset to last page if current page exceeds total pages
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handlePageSizeChange = (newSize: string) => {
    const size = parseInt(newSize);
    setPageSize(size);
    setCurrentPage(1);
  };

  const pageNumbers = getPageNumbers(currentPage, totalPages);

  const handleViewDispute = (dispute: DisputeUI) => {
    // Navigate to full-screen dispute details page
    navigate(`/disputes/all/${dispute.id}`, {
      state: { from: 'all' },
    });
  };

  const handleExportToExcel = async () => {
    if (filteredDisputes.length === 0) return;
    setIsExporting(true);
    try {
      exportDisputesToExcel(
        filteredDisputes,
        selectedCompany?.currency || 'GBP',
        `disputes-${new Date().toISOString().split('T')[0]}.csv`,
        visibleColumns,
      );
    } catch (error) {
      console.error('Error exporting disputes:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportAllColumns = async () => {
    if (filteredDisputes.length === 0) return;
    setIsExporting(true);
    try {
      exportDisputesToExcel(
        filteredDisputes,
        selectedCompany?.currency || 'GBP',
        `disputes-all-columns-${new Date().toISOString().split('T')[0]}.csv`,
      );
    } catch (error) {
      console.error('Error exporting disputes:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const handleNextAction = () => {
    setIsNextActionModalOpen(true);
  };

  const handleProceedToNextStage = async () => {
    if (!nextActionNotes.trim()) {
      toast.error('Notes are required');
      return;
    }

    if (!selectedDispute) {
      toast.error('No dispute selected');
      return;
    }

    const newLevel = selectedDispute.currentLevel + 1;

    // Calculate new DueDate from the new level's followUpDays
    // Prefer snapshot stages (captured at dispute creation), fall back to live config.
    let newDueDate: string | undefined;
    const followUpDays = resolveFollowUpDaysForLevel(newLevel, snapshotStages, fullSelectedDisputeType);
    if (followUpDays && typeof followUpDays === 'number') {
      const due = new Date();
      due.setDate(due.getDate() + followUpDays);
      newDueDate = due.toISOString().split('T')[0];
    }

    // Extract assigned user from the next stage's snapshot data (only when not resolving).
    // A credit controller stage is left to the backend, which resolves it from the customer.
    let assignedToUserId: number | undefined;
    let usesCreditController = false;
    if (
      (!selectedDispute.totalLevels || newLevel !== selectedDispute.totalLevels) &&
      snapshotStages &&
      snapshotStages.length > 0
    ) {
      const nextSnapshotStage = snapshotStages.find((s) => s.sortOrder === newLevel);
      ({ assignedToUserId, usesCreditController } = readStageAssignee(nextSnapshotStage?.properties, activeUsers));
    }

    const request: UpdateDisputeRequest = {
      currentLevel: newLevel,
      dueDate: newDueDate,
      ...(assignedToUserId ? { assignedToUserId } : {}),
    };

    // If the new level equals totalLevels, set status to Resolved (statusId = 5)
    if (selectedDispute.totalLevels && newLevel === selectedDispute.totalLevels) {
      request.statusId = 5;
    }

    try {
      const response = await updateDisputeMutation.mutateAsync(request);

      if (usesCreditController && !response.assignedTo) {
        toast.warning(`${selectedDispute.customer} has no Credit Controller assigned - the dispute is unassigned`, {
          duration: 8000,
        });
      }

      const resolvedStages = resolveWorkflowStagesForDispute(selectedDispute, snapshotStages);
      const nextStage = resolvedStages.find((s) => s.level === newLevel);

      if (nextStage) {
        toast.success(
          selectedDispute.totalLevels && newLevel === selectedDispute.totalLevels
            ? 'Dispute resolved - all workflow stages completed'
            : `Successfully moved to: ${nextStage.title}`,
        );
      }

      // Close modal and reset
      setIsNextActionModalOpen(false);
      setNextActionNotes('');
      setNextActionFollowUpDate('');

      // Refresh dispute data
      refetchDisputes();
      refetchActivities();
    } catch (error) {
      console.error('Failed to proceed to next stage:', error);
      toast.error('Failed to advance to next stage');
    }
  };

  const handleCancelNextAction = () => {
    setIsNextActionModalOpen(false);
    setNextActionNotes('');
    setNextActionFollowUpDate('');
  };

  // ── Quick action handlers ──────────────────────────────────────────────────

  const handleQuickAddNote = async (content: string) => {
    if (selectedDisputeNumericId) {
      try {
        await addDisputeNoteMutation.mutateAsync({
          disputeId: selectedDisputeNumericId.toString(),
          content,
          userId: '0',
          userName: 'Current User',
        });
        toast.success('Note added successfully');
        refetchDisputeNotes();
      } catch {
        toast.error('Failed to add note');
        throw new Error('Failed to add note');
      }
    } else {
      // Fallback for demo mode — refetch notes (hook returns mock data automatically)
      refetchDisputeNotes();
      toast.success('Note added successfully');
    }
  };

  const handleQuickReassign = async () => {
    if (!reassignUserId) return;
    const user = activeUsers.find((u) => u.id.toString() === reassignUserId);
    const userName = user ? `${user.firstName} ${user.lastName}` : reassignUserId;
    const count = selectedDisputeIds.length;
    await quickUpdateDisputes.mutateAsync(
      selectedDisputeIds.map((id) => {
        const d = filteredDisputes.find((x) => x.id === id);
        return {
          disputeId: parseInt(id, 10),
          disputeNumber: d?.disputeNumber ?? id,
          assignedTo: userName,
          assignedToUserId: parseInt(reassignUserId, 10),
        };
      }),
    );
    toast.success(`${count} dispute${count > 1 ? 's' : ''} reassigned to ${userName}`);
    setReassignUserId('');
    setReassignOpen(false);
    refetchDisputes();
  };

  const handleQuickUpdatePriority = async () => {
    if (!updatePriorityValue) return;
    const count = selectedDisputeIds.length;
    const matchedPriority = disputePriorities.find((p) => p.name === updatePriorityValue);
    await quickUpdateDisputes.mutateAsync(
      selectedDisputeIds.map((id) => {
        const d = filteredDisputes.find((x) => x.id === id);
        return {
          disputeId: parseInt(id, 10),
          disputeNumber: d?.disputeNumber ?? id,
          priority: updatePriorityValue,
          priorityId: matchedPriority?.id,
        };
      }),
    );
    toast.success(`Priority updated to ${updatePriorityValue} for ${count} dispute${count > 1 ? 's' : ''}`);
    setUpdatePriorityValue('');
    setUpdatePriorityOpen(false);
    refetchDisputes();
  };

  const handleQuickCloseDispute = async () => {
    if (closeReason === 'other' && !closeNote.trim()) return;
    const disputeId = selectedDispute?.id;
    if (!disputeId) return;
    const closeReasonLabels = {
      duplicate: 'Duplicate Entry',
      customer_withdrew: 'Customer Withdrew Dispute',
      invalid_dispute: 'Invalid Dispute',
      other: 'Other',
    };
    const composedCloseNote = closeNote
      ? `Dispute closed - Reason: ${closeReasonLabels[closeReason]}\n\nNote: ${closeNote}`
      : `Dispute closed - Reason: ${closeReasonLabels[closeReason]}`;
    await quickUpdateDisputes.mutateAsync([
      {
        disputeId: selectedDisputeNumericId,
        disputeNumber: selectedDispute?.disputeNumber ?? disputeId,
        status: 'Closed',
        closedReason: closeReason,
        closedDate: new Date().toISOString().split('T')[0],
        closedNote: composedCloseNote,
      },
    ]);
    if (selectedDisputeNumericId) {
      await addDisputeNoteMutation.mutateAsync({
        disputeId: selectedDisputeNumericId.toString(),
        content: composedCloseNote,
        userId: '0',
        userName: 'Current User',
      });
    }
    toast.success(`Dispute ${selectedDispute?.disputeNumber ?? disputeId} closed`);
    setCloseReason('duplicate');
    setCloseNote('');
    setCloseDisputeOpen(false);
    setSelectedDisputeIds([]);
    setSelectedDispute(null);
    refetchDisputes();
    refetchActivities();
  };

  const handleQuickResolveDispute = useCallback(
    async (outcome: 'upheld' | 'denied' | 'partial', amount: string, notes: string) => {
      const disputeId = selectedDispute?.id;
      if (!disputeId) return;
      await quickUpdateDisputes.mutateAsync([
        {
          disputeId: selectedDisputeNumericId,
          disputeNumber: selectedDispute?.disputeNumber ?? disputeId,
          status: 'Resolved',
          resolutionNotes: notes,
          resolutionOutcome: outcome,
          resolutionAmount: outcome === 'partial' ? parseFloat(amount) : (selectedDispute?.amount ?? null),
          resolutionDate: new Date().toISOString().split('T')[0],
          resolvedBy: 'Current User',
        },
      ]);
      toast.success(`Dispute ${selectedDispute?.disputeNumber ?? disputeId} marked as resolved`);
      setResolveDisputeOpen(false);
      setSelectedDisputeIds([]);
      setSelectedDispute(null);
      refetchDisputes();
      refetchActivities();
    },
    [selectedDispute, quickUpdateDisputes, refetchDisputes, refetchActivities],
  );

  const handleReopenDispute = async () => {
    const updates = selectedDisputeObjects.map((d) => ({
      disputeId: parseInt(d.id, 10),
      disputeNumber: d.disputeNumber,
      status: d.currentLevel === 1 ? 'New' : 'In Progress',
    }));
    await quickUpdateDisputes.mutateAsync(updates);
    const count = updates.length;
    toast.success(`${count} dispute${count > 1 ? 's' : ''} reopened`);
    setReopenDisputeOpen(false);
    setSelectedDisputeIds([]);
    setSelectedDispute(null);
    refetchDisputes();
  };

  // Toggle a single row
  const handleToggleRow = (dispute: DisputeUI) => {
    setSelectedDisputeIds((prev) => {
      if (prev.includes(dispute.id)) {
        const next = prev.filter((id) => id !== dispute.id);
        setSelectedDispute(next.length === 1 ? (filteredDisputes.find((d) => d.id === next[0]) ?? null) : null);
        return next;
      } else {
        const next = [...prev, dispute.id];
        setSelectedDispute(next.length === 1 ? dispute : null);
        return next;
      }
    });
  };

  // Select / deselect all on current page
  const allPageSelected =
    filteredDisputes.length > 0 && filteredDisputes.every((d) => selectedDisputeIds.includes(d.id));
  const somePageSelected = filteredDisputes.some((d) => selectedDisputeIds.includes(d.id)) && !allPageSelected;

  const handleToggleAll = () => {
    if (allPageSelected) {
      setSelectedDisputeIds((prev) => prev.filter((id) => !filteredDisputes.some((d) => d.id === id)));
      setSelectedDispute(null);
    } else {
      const pageIds = filteredDisputes.map((d) => d.id);
      setSelectedDisputeIds((prev) => Array.from(new Set([...prev, ...pageIds])));
      setSelectedDispute(null);
    }
  };

  const renderNextActionPanel = () => {
    if (!selectedDispute) return null;
    const resolvedStages = resolveWorkflowStagesForDispute(selectedDispute, snapshotStages);
    const currentStage = resolvedStages.find((s) => s.level === selectedDispute.currentLevel);
    const nextStage = resolvedStages.find((s) => s.level === selectedDispute.currentLevel + 1);

    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Zap className="h-4 w-4 text-amber-600 dark:text-amber-400" />
            Next Action
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {currentStage && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold">{currentStage.title}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Current Stage (Level {currentStage.level} of {resolvedStages.length})
                  </p>
                </div>
                <Badge
                  variant="outline"
                  className="bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800"
                >
                  In Progress
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground">{currentStage.description}</p>
            </div>
          )}

          {nextStage && (
            <div className="pt-4 border-t space-y-3">
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Next Stage</p>
                <p className="text-sm font-semibold">{nextStage.title}</p>
                <p className="text-xs text-muted-foreground mt-1">{nextStage.description}</p>
                {Boolean(nextStage.estimatedDays) && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Estimated: {nextStage.estimatedDays} day{nextStage.estimatedDays !== 1 ? 's' : ''}
                  </p>
                )}
              </div>
              <Button className="w-full gap-2" onClick={handleNextAction}>
                <CheckCircle2 className="h-4 w-4" />
                Complete {currentStage?.title || 'Current Stage'} & Advance
              </Button>
            </div>
          )}

          {!nextStage && selectedDispute.currentLevel === resolvedStages.length && (
            <div className="pt-4 border-t">
              <div className="flex items-center gap-2 text-green-600 dark:text-green-400 mb-2">
                <CheckCircle2 className="h-4 w-4" />
                <p className="text-sm font-semibold">Final Stage Reached</p>
              </div>
              <p className="text-xs text-muted-foreground mb-3">
                All workflow stages have been completed. Ready to close dispute.
              </p>
              <Button
                className="w-full gap-2 bg-green-600 hover:bg-green-700"
                onClick={() => {
                  // TODO: implement close dispute from final stage
                }}
              >
                <CheckCircle2 className="h-4 w-4" />
                Close Dispute
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  const renderReopenDialog = () => (
    <Dialog open={reopenDisputeOpen} onOpenChange={setReopenDisputeOpen}>
      <DialogContent className="sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RotateCcw className="h-5 w-5 text-primary" />
            Reopen Dispute{selectedDisputeIds.length > 1 ? 's' : ''}
          </DialogTitle>
          <DialogDescription>{renderReopenDialogDescription()}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => setReopenDisputeOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleReopenDispute} className="gap-2">
            <RotateCcw className="h-4 w-4" />
            Confirm Reopen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  const totalDisputes = totals.totalCount;
  const activeDisputes = totals.activeCount;
  const totalAmount = totals.totalAmount;
  const previousMonthCount = totals.previousMonthCount;
  const newThisWeekCount = totals.newThisWeekCount;

  const monthChangePercent =
    previousMonthCount > 0 ? Math.round(((totalDisputes - previousMonthCount) / previousMonthCount) * 100) : 0;
  const monthChangeIsPositive = monthChangePercent > 0;
  const monthChangeLabel = monthChangeIsPositive ? `+${monthChangePercent}%` : `${monthChangePercent}%`;

  return (
    <div className="space-y-6 p-6" style={{ minHeight: '100vh', backgroundColor: 'var(--background)' }}>
      {/* Header Section */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-3xl font-bold tracking-tight">Dispute Management</h1>
          <Badge variant="outline" className="ml-2">
            {totalRecords} disputes
          </Badge>
          <p className="text-muted-foreground">Track, manage, and resolve customer disputes</p>
        </div>
        <div className="flex items-center gap-2">
          <HelpSupportButton screenId="disputes-all" />
          <FeatureFeedbackCollector feature="Disputes" />
        </div>
      </div>

      {/* KPI Cards */}
      <div className="shrink-0 grid gap-4 md:grid-cols-3">
        <KPICard
          title="Total Disputes"
          value={totalDisputes}
          subtitle={`${monthChangeLabel} from last month`}
          icon={Activity}
          colorScheme="blue"
          isLoading={isLoading}
        />
        <KPICard
          title="Active Disputes"
          value={activeDisputes}
          subtitle={`${newThisWeekCount} new this week`}
          icon={Clock}
          colorScheme="amber"
          isLoading={isLoading}
        />
        <KPICard
          title="Disputed Amount"
          value={`${currencySymbol}${totalAmount.toLocaleString()}`}
          subtitle="Across all active disputes"
          icon={getCurrencyIcon(selectedCompany?.currency)}
          colorScheme="green"
          isLoading={isLoading}
        />
      </div>

      {/* Table Section */}
      <div className="flex flex-col flex-1 min-h-0">
        {/* Sticky toolbar */}
        <div className="sticky top-0 z-20 bg-background border rounded-t-xl px-4 py-3">
          <div className="flex items-center gap-4">
            <span className="text-lg font-bold text-gray-900 whitespace-nowrap">Disputes ({totalRecords})</span>
            <div className="relative flex-1 min-w-[200px]">
              {!searchQuery && (
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground pointer-events-none" />
              )}
              <Input
                placeholder="Search disputes..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
                className={searchQuery ? 'pr-9' : 'pl-10'}
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => {
                    setSearchQuery('');
                    setCurrentPage(1);
                  }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <Button variant="outline" onClick={handleOpenMoreFilters}>
              <Filter className="mr-2 h-4 w-4" />
              Filters
              {activeFilterCount > 0 && (
                <Badge variant="secondary" className="ml-1">
                  {activeFilterCount}
                </Badge>
              )}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  disabled={isExporting || filteredDisputes.length === 0}
                  title="Export"
                >
                  <FileSpreadsheet className={cn('h-4 w-4', isExporting && 'animate-pulse')} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={handleExportToExcel}>Export Current View</DropdownMenuItem>
                <DropdownMenuItem onClick={handleExportAllColumns}>Export All Columns</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsColumnConfigOpen(true)}
              className="h-8 w-8"
              title="Configure columns"
            >
              <Settings className="h-4 w-4" />
            </Button>
          </div>
          {activeFilterCount === 0 && !debouncedSearch && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground bg-blue-50 px-3 py-1.5 rounded-md border border-blue-200 mt-2">
              <Filter className="h-3.5 w-3.5 text-blue-600" />
              <span className="text-blue-700">
                Default filter active: <span className="font-medium">Excluding Closed</span>
              </span>
              <button
                onClick={() => {
                  setAdvancedFilters({ ...advancedFilters, statuses: [] });
                  toast.info('Default filter removed');
                }}
                className="ml-1 text-blue-600 hover:text-blue-800 transition-colors"
                title="Remove default filter"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
        </div>

        {/* Table card */}
        <Card className="rounded-t-none border-t-0 flex flex-col flex-1 min-h-0">
          <CardContent className="p-0 flex flex-col flex-1 min-h-0">
            {/* Scrollable table area */}
            <div className="flex-1 min-h-[200px] overflow-auto">
              <table className="w-full caption-bottom text-sm">
                <thead className="sticky top-0 z-10 bg-white shadow-sm">
                  <tr>
                    <th className="h-10 text-center font-bold text-gray-700 w-[40px] min-w-[40px] max-w-[40px] border-r border-gray-200">
                      <div className="flex items-center justify-center h-full">
                        <Checkbox
                          checked={allPageSelected}
                          onCheckedChange={handleToggleAll}
                          aria-label="Select all disputes on this page"
                          className={cn(somePageSelected && !allPageSelected && 'data-[state=checked]:bg-primary')}
                        />
                      </div>
                    </th>
                    {visibleColumns.map((columnId) => {
                      const headerMap: Record<string, string> = {
                        disputeId: 'Dispute ID',
                        customer: 'Customer',
                        type: 'Type',
                        amount: 'Amount',
                        status: 'Status',
                        priority: 'Priority',
                        workflowProgress: 'Workflow Progress',
                        assignedTo: 'Assigned To',
                        dueDate: 'Due Date',
                        createdDate: 'Created Date',
                        workflow: 'Workflow',
                        description: 'Description',
                      };
                      const label = headerMap[columnId] || columnId;
                      const isSortable = !!sortableColumnMap[columnId];
                      const colWidth = localColumnWidths[columnId];
                      const isDragOver = dragOverColumn === columnId && draggedColumn !== columnId;
                      const isPinLeft = pinnedLeft.includes(columnId);
                      const isPinRight = pinnedRight.includes(columnId);
                      const pinLeftOffset = pinnedLeft.indexOf(columnId) * (colWidth || 150);
                      const pinRightOffset = pinnedRight.indexOf(columnId) * (colWidth || 150);
                      return (
                        <th
                          key={columnId}
                          draggable
                          onDragStart={() => setDraggedColumn(columnId)}
                          onDragOver={(e) => {
                            e.preventDefault();
                            setDragOverColumn(columnId);
                          }}
                          onDragEnd={() => {
                            setDraggedColumn(null);
                            setDragOverColumn(null);
                          }}
                          onDrop={() => {
                            if (!draggedColumn || draggedColumn === columnId) return;
                            const newCols = [...visibleColumns];
                            const fromIdx = newCols.indexOf(draggedColumn);
                            const toIdx = newCols.indexOf(columnId);
                            newCols.splice(fromIdx, 1);
                            newCols.splice(toIdx, 0, draggedColumn);
                            setColumnOrder(newCols);
                            setDraggedColumn(null);
                            setDragOverColumn(null);
                          }}
                          style={{
                            width: colWidth ? `${colWidth}px` : undefined,
                            minWidth: colWidth ? `${colWidth}px` : undefined,
                            position: isPinLeft || isPinRight ? 'sticky' : 'relative',
                            left: isPinLeft ? `${pinLeftOffset}px` : undefined,
                            right: isPinRight ? `${pinRightOffset}px` : undefined,
                            zIndex: isPinLeft || isPinRight ? 20 : undefined,
                          }}
                          className={cn(
                            'h-10 px-4 text-left font-bold text-gray-700 border-r border-gray-200 last:border-r-0 select-none bg-white',
                            isDragOver && 'bg-blue-50 border-l-2 border-l-blue-400',
                            draggedColumn === columnId && 'opacity-50',
                            (isPinLeft || isPinRight) && 'shadow-md',
                            columnId === 'amount' && 'text-right',
                          )}
                        >
                          <div className="flex items-center justify-between gap-1 w-full">
                            {isSortable ? (
                              <button
                                onClick={() => handleSort(columnId)}
                                className="flex items-center gap-1 hover:text-gray-900 transition-colors font-bold"
                              >
                                <span>{label}</span>
                                <span className="text-gray-500">{getSortIcon(columnId)}</span>
                              </button>
                            ) : (
                              <span className="font-bold">{label}</span>
                            )}
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                <button className="p-0.5 rounded hover:bg-gray-100 transition-colors cursor-pointer flex-shrink-0">
                                  <MoreVertical className="h-4 w-4 text-gray-300 hover:text-gray-500" />
                                </button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="z-[9999] w-48">
                                <DropdownMenuItem onClick={() => handlePinLeft(columnId)}>
                                  <Pin className="h-4 w-4 mr-2" />
                                  {isPinLeft ? 'Unpin from Left' : 'Pin to Left'}
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handlePinRight(columnId)}>
                                  <Pin className="h-4 w-4 mr-2 rotate-180" />
                                  {isPinRight ? 'Unpin from Right' : 'Pin to Right'}
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={handleResetColumnWidths}>
                                  <Maximize2 className="h-4 w-4 mr-2" />
                                  Reset Column Widths
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={handleResetColumnOrder}>
                                  <RotateCcw className="h-4 w-4 mr-2" />
                                  Reset Column Order
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                          {/* Resize handle */}
                          <button
                            type="button"
                            aria-label="Resize column"
                            className="absolute right-0 top-0 h-full w-2 cursor-col-resize hover:bg-blue-300 opacity-0 hover:opacity-60 transition-opacity z-10 p-0 border-0 bg-transparent"
                            onMouseDown={(e) =>
                              handleResizeMouseDown(
                                e,
                                columnId,
                                colWidth || (e.currentTarget.closest('th') as HTMLElement)?.offsetWidth || 150,
                              )
                            }
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => e.stopPropagation()}
                          />
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {isLoading && (
                    <tr>
                      <td colSpan={visibleColumns.length + 1} className="h-64 text-center">
                        <div className="flex flex-col items-center justify-center space-y-4">
                          <Loader2 className="h-8 w-8 animate-spin text-primary" />
                          <p className="text-sm text-muted-foreground">Loading disputes...</p>
                        </div>
                      </td>
                    </tr>
                  )}
                  {!isLoading && filteredDisputes.length === 0 && (
                    <tr>
                      <td colSpan={visibleColumns.length + 1} className="h-64 text-center">
                        <p className="text-sm text-muted-foreground">No disputes found</p>
                      </td>
                    </tr>
                  )}
                  {!isLoading &&
                    filteredDisputes.length > 0 &&
                    filteredDisputes.map((dispute, index) => (
                      <tr
                        key={dispute.id}
                        className={cn(
                          index % 2 === 0 ? 'bg-white' : 'bg-gray-50',
                          selectedDisputeIds.includes(dispute.id) && 'bg-muted/50',
                          'hover:bg-muted/30 transition-colors',
                        )}
                      >
                        <td className="h-10 text-center w-[40px] min-w-[40px] max-w-[40px] border-r border-gray-100">
                          <div className="flex items-center justify-center h-full">
                            <Checkbox
                              checked={selectedDisputeIds.includes(dispute.id)}
                              onCheckedChange={() => handleToggleRow(dispute)}
                              aria-label={`Select dispute ${dispute.disputeNumber}`}
                            />
                          </div>
                        </td>
                        {visibleColumns.map((colId) => {
                          const colWidth = localColumnWidths[colId];
                          const isPinLeft = pinnedLeft.includes(colId);
                          const isPinRight = pinnedRight.includes(colId);
                          const pinLeftOffset = pinnedLeft.indexOf(colId) * (colWidth || 150);
                          const pinRightOffset = pinnedRight.indexOf(colId) * (colWidth || 150);
                          // Only a resized column clips; until then it still grows to fit its content.
                          const { title, content } = clipCell(renderColumnCell(colId, dispute), colWidth);
                          return (
                            <td
                              key={colId}
                              title={title}
                              style={{
                                width: colWidth ? `${colWidth}px` : undefined,
                                minWidth: colWidth ? `${colWidth}px` : undefined,
                                position: isPinLeft || isPinRight ? 'sticky' : undefined,
                                left: isPinLeft ? `${pinLeftOffset}px` : undefined,
                                right: isPinRight ? `${pinRightOffset}px` : undefined,
                                zIndex: isPinLeft || isPinRight ? 10 : undefined,
                              }}
                              className={cn(
                                'px-4 py-2 align-middle border-r border-gray-100 last:border-r-0 whitespace-nowrap',
                                (isPinLeft || isPinRight) && 'shadow-md',
                                index % 2 === 0 ? 'bg-white' : 'bg-gray-50',
                              )}
                            >
                              {content}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>

            {/* Pagination footer */}
            <div className="shrink-0 border-t px-4 py-3 flex items-center justify-between">
              <span className="text-sm text-muted-foreground">
                Page {currentPage} of {totalPages}
              </span>
              <div className="flex items-center gap-3">
                <span className="text-sm text-muted-foreground">Rows per page:</span>
                <Select value={pageSize.toString()} onValueChange={handlePageSizeChange}>
                  <SelectTrigger className="h-8 w-[80px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="25">25</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                    <SelectItem value="200">200</SelectItem>
                  </SelectContent>
                </Select>
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  {pageNumbers.map((page) =>
                    typeof page === 'string' ? (
                      <span key={page} className="px-1 text-muted-foreground">
                        &hellip;
                      </span>
                    ) : (
                      <Button
                        key={`page-${page}`}
                        variant={currentPage === page ? 'default' : 'outline'}
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => handlePageChange(page)}
                      >
                        {page}
                      </Button>
                    ),
                  )}
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Column Configuration Dialog */}
      <ColumnConfigDialog
        open={isColumnConfigOpen}
        onOpenChange={setIsColumnConfigOpen}
        columns={disputeColumns}
        onApply={saveDisputeColumnPreferences}
        onReset={resetDisputeColumnPreferences}
      />

      {/* ── Floating Quick Actions Toolbar ─────────────────────────────────── */}
      {selectedDisputeIds.length > 0 &&
        (() => {
          const selectedObjs = selectedDisputeIds
            .map((id) => filteredDisputes.find((d) => d.id === id))
            .filter((d): d is DisputeUI => d !== undefined);
          const totalAmount = selectedObjs.reduce((sum, d) => sum + d.amount, 0);
          return (
            <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 animate-in slide-in-from-bottom-5 duration-300">
              <div className="bg-primary text-primary-foreground rounded-lg shadow-2xl border border-primary/20">
                <div className="flex items-center gap-4 px-6 py-4">
                  {/* Selection Info */}
                  <div className="flex items-center gap-3 border-r border-primary-foreground/20 pr-4">
                    <div className="flex flex-col">
                      <div className="flex items-center gap-2">
                        <Badge
                          variant="secondary"
                          className="bg-primary-foreground/20 text-primary-foreground hover:bg-primary-foreground/30"
                        >
                          {selectedDisputeIds.length}
                        </Badge>
                        <span className="font-medium">
                          {selectedDisputeIds.length === 1 ? 'dispute' : 'disputes'} selected
                        </span>
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-sm text-primary-foreground/80">
                        <span>
                          Total:{' '}
                          <span className="font-semibold">
                            {formatCurrency(totalAmount, selectedCompany?.currency || 'GBP')}
                          </span>
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    {renderActionsDropdown()}

                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setSelectedDisputeIds([]);
                        setSelectedDispute(null);
                      }}
                      className="gap-2 hover:bg-primary-foreground/20"
                    >
                      <X className="h-4 w-4" />
                      Clear
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

      {/* Log Call Modal */}
      <LogCallModal
        open={logCallOpen}
        onOpenChange={setLogCallOpen}
        customerName={selectedDispute?.customer || ''}
        customerPhone={selectedDispute?.contactPhone}
        customerId={selectedDispute?.customerId ? String(selectedDispute.customerId) : undefined}
        disputeId={selectedDisputeNumericId ? selectedDisputeNumericId.toString() : undefined}
        onSuccess={() => {
          // Toast is already shown by the modal's internal handleSave
        }}
      />

      {/* Send Email Modal */}
      <DisputeSendEmailModal
        open={sendEmailOpen}
        onOpenChange={setSendEmailOpen}
        disputeId={selectedDisputeNumericId}
        customerId={selectedDispute?.customerId ?? 0}
        customerName={selectedDispute?.customer || ''}
        accountNumber={selectedDispute?.customerAccountCode || ''}
        onSuccess={() => {
          refetchActivities();
        }}
      />

      {/* ── Add Note Modal ─────────────────────────────────────────────────── */}
      <AddNoteModal
        open={addNoteOpen}
        onOpenChange={setAddNoteOpen}
        disputeId={selectedDispute?.id || ''}
        disputeLabel={selectedDispute?.id || ''}
        customerName={selectedDispute?.customer || ''}
        onSubmit={handleQuickAddNote}
        isPending={addDisputeNoteMutation.isPending}
      />

      {/* ── Reassign User Modal ─────────────────────────────────────────────── */}
      <Dialog
        open={reassignOpen}
        onOpenChange={(o) => {
          setReassignOpen(o);
          if (!o) setReassignUserId('');
        }}
      >
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCog className="h-5 w-5 text-primary" />
              Reassign User
            </DialogTitle>
            <DialogDescription>
              {selectedDisputeIds.length === 1
                ? `Reassign dispute ${selectedDispute?.disputeNumber} to a different user`
                : `Reassign ${selectedDisputeIds.length} disputes to a different user`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Label>Assign To</Label>
            <Select value={reassignUserId} onValueChange={setReassignUserId}>
              <SelectTrigger>
                <SelectValue placeholder="Select a user..." />
              </SelectTrigger>
              <SelectContent>
                {activeUsers.map((user) => (
                  <SelectItem key={user.id} value={user.id.toString()}>
                    {user.firstName} {user.lastName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setReassignOpen(false);
                setReassignUserId('');
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleQuickReassign} disabled={!reassignUserId} className="gap-2">
              <UserCog className="h-4 w-4" />
              Reassign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Update Priority Modal ───────────────────────────────────────────── */}
      <UpdatePriorityModal
        open={updatePriorityOpen}
        onOpenChange={(o) => {
          setUpdatePriorityOpen(o);
          if (!o) setUpdatePriorityValue('');
        }}
        selectedCount={selectedDisputeIds.length}
        selectedDisputeId={selectedDispute?.id}
        priorities={disputePriorities}
        value={updatePriorityValue}
        onValueChange={setUpdatePriorityValue}
        onConfirm={handleQuickUpdatePriority}
      />

      {/* ── Resolve Dispute Modal ───────────────────────────────────────────── */}
      <ResolveDisputeModal
        open={resolveDisputeOpen}
        onOpenChange={setResolveDisputeOpen}
        disputeId={selectedDispute?.id}
        customerName={selectedDispute?.customer}
        onResolve={handleQuickResolveDispute}
      />

      {/* ── Close Dispute Modal ─────────────────────────────────────────────── */}
      <Dialog
        open={closeDisputeOpen}
        onOpenChange={(o) => {
          setCloseDisputeOpen(o);
          if (!o) {
            setCloseReason('duplicate');
            setCloseNote('');
          }
        }}
      >
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Close Dispute</DialogTitle>
            <DialogDescription>
              Close this dispute without completing all workflow steps. This action will mark the dispute as closed and
              remove it from active views.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="close-reason">Reason for Closing</Label>
              <Select
                value={closeReason}
                onValueChange={(value) =>
                  setCloseReason(value as 'duplicate' | 'customer_withdrew' | 'invalid_dispute' | 'other')
                }
              >
                <SelectTrigger id="close-reason">
                  <SelectValue placeholder="Select a reason" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="duplicate">Duplicate Entry</SelectItem>
                  <SelectItem value="customer_withdrew">Customer Withdrew Dispute</SelectItem>
                  <SelectItem value="invalid_dispute">Invalid Dispute</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="close-note">
                Additional Notes {closeReason === 'other' && <span className="text-destructive">*</span>}
              </Label>
              <Textarea
                id="close-note"
                placeholder="Provide additional context for closing this dispute..."
                value={closeNote}
                onChange={(e) => setCloseNote(e.target.value)}
                rows={4}
              />
              {closeReason === 'other' && !closeNote && (
                <p className="text-sm text-muted-foreground">Note is required when selecting "Other" as the reason.</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setCloseDisputeOpen(false);
                setCloseReason('duplicate');
                setCloseNote('');
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleQuickCloseDispute}
              disabled={closeReason === 'other' && !closeNote}
            >
              <XCircle className="mr-2 h-4 w-4" />
              Close Dispute
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reopen Dispute Confirmation Dialog */}
      {renderReopenDialog()}

      {/* Next Action Modal */}
      <Dialog open={isNextActionModalOpen} onOpenChange={setIsNextActionModalOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-primary" />
              Proceed to Next Stage
            </DialogTitle>
            <DialogDescription>Complete the current stage and advance the dispute workflow</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Next Stage Info */}
            {selectedDispute &&
              (() => {
                const resolvedStages = resolveWorkflowStagesForDispute(selectedDispute, snapshotStages);
                const nextStage = resolvedStages.find((s) => s.level === selectedDispute.currentLevel + 1);

                return nextStage ? (
                  <div className="rounded-lg bg-blue-50 dark:bg-blue-950 p-4 space-y-2">
                    <div className="flex items-center gap-2">
                      <Zap className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                      <p className="font-semibold text-sm text-blue-900 dark:text-blue-100">{nextStage.title}</p>
                    </div>
                    <p className="text-xs text-blue-700 dark:text-blue-300">{nextStage.description}</p>
                    {Boolean(nextStage.estimatedDays) && (
                      <p className="text-xs text-blue-600 dark:text-blue-400">
                        Estimated time: {nextStage.estimatedDays} day{nextStage.estimatedDays !== 1 ? 's' : ''}
                      </p>
                    )}
                  </div>
                ) : null;
              })()}

            {/* Action Notes */}
            <div className="space-y-2">
              <Label htmlFor="action-notes" className="flex items-center gap-1">
                Action Notes <span className="text-destructive">*</span>
              </Label>
              <Textarea
                id="action-notes"
                placeholder="Enter notes about the actions taken and any relevant details..."
                value={nextActionNotes}
                onChange={(e) => setNextActionNotes(e.target.value)}
                rows={4}
                className="resize-none"
              />
              <p className="text-xs text-muted-foreground">These notes will be added to the activity timeline</p>
            </div>

            {/* Follow-up Date */}
            <div className="space-y-2">
              <Label htmlFor="follow-up-date">Follow-up Date (Optional)</Label>
              <Input
                id="follow-up-date"
                type="date"
                value={nextActionFollowUpDate}
                onChange={(e) => setNextActionFollowUpDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
              />
              <p className="text-xs text-muted-foreground">Set a reminder for the next action</p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleCancelNextAction}>
              Cancel
            </Button>
            <Button onClick={handleProceedToNextStage} disabled={!nextActionNotes.trim()} className="gap-2">
              <CheckCircle2 className="h-4 w-4" />
              Proceed
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Filter Drawer */}
      <Sheet open={moreFiltersOpen} onOpenChange={handleCloseMoreFilters}>
        <SheetContent side="right" className="w-full sm:max-w-lg overflow-hidden flex flex-col h-full">
          <SheetHeader>
            <SheetTitle>Filters</SheetTitle>
            <SheetDescription>Narrow down your disputes view</SheetDescription>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto px-4 mt-6">
            <div className="space-y-4 pb-4">
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Status</Label>
                <div className="space-y-3">
                  {DISPUTE_STATUS_OPTIONS.map((status) => (
                    <label key={status} className="flex items-center gap-3 cursor-pointer">
                      <Checkbox
                        checked={draftFilters.statuses.includes(status)}
                        onCheckedChange={(checked) => handleToggleDraftStatus(status, !!checked)}
                      />
                      <span className="text-sm">{status}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Priority</Label>
                <div className="space-y-3">
                  {disputePriorities.map((p) => (
                    <label key={p.code} className="flex items-center gap-3 cursor-pointer">
                      <Checkbox
                        checked={draftFilters.priorities.includes(p.name)}
                        onCheckedChange={(checked) => handleToggleDraftPriority(p.name, !!checked)}
                      />
                      <span className="text-sm">{p.name}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Assigned User</Label>
                <Select
                  value={draftFilters.assignedUser}
                  onValueChange={(val) => setDraftFilters((prev) => ({ ...prev, assignedUser: val }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All Users" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Users</SelectItem>
                    {userFilterOptions.map((user) => (
                      <SelectItem key={user.id} value={String(user.id)}>
                        {user.displayName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="border-t pt-4 mt-4">
                <Label className="text-sm font-semibold mb-3 block">Quick Filters</Label>
                <div className="space-y-3">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <Checkbox
                      checked={draftFilters.overdueOnly}
                      onCheckedChange={(checked) => {
                        setDraftFilters((prev) => ({ ...prev, overdueOnly: !!checked }));
                      }}
                    />
                    <span className="text-sm">Overdue Only</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
          <SheetFooter className="p-6 bg-background border-t">
            <div className="flex gap-2 w-full">
              <Button variant="outline" className="flex-1" onClick={handleClearDraftFilters}>
                Clear All
              </Button>
              <Button className="flex-1" onClick={handleApplyFilters}>
                Apply Filters
              </Button>
            </div>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
}
